// Bluetooth for the equipment code (issue #217, phase 2).
//
// In a browser this is navigator.bluetooth, untouched. Inside the Android app
// it's a small stand-in with the same shape, built on the native
// @capacitor-community/bluetooth-le plugin, so ConnectedEquipmentContext,
// bleHrSession, ftmsDeviceSession and the parsers in utils/ble and utils/ftms
// run unchanged on either. Only the slice of Web Bluetooth they use is here:
//
//   bluetooth.requestDevice({ filters, optionalServices }) / getDevices()
//   device.id / name / gatt.connect() / gatt.disconnect()
//   device "gattserverdisconnected" events
//   server.getPrimaryService(uuid)
//   service.getCharacteristic(uuid) / getCharacteristics()
//   characteristic.uuid / readValue() / startNotifications() /
//     stopNotifications() / "characteristicvaluechanged" events (event.target.value)
//
// What native gives us over Chrome: device ids are the Bluetooth address, so
// remembered gear reconnects after the app restarts with no picker and no
// chrome://flags (#169), and phase 3 can keep the strap in a foreground service.

import { BleClient, numberToUUID } from "@capacitor-community/bluetooth-le";
import { isNativeApp } from "./native";
import { listPersistedDevices } from "../hooks/ftmsDeviceSession";
import { readPersistedHrm } from "../hooks/bleHrSession";

/**
 * The Bluetooth to use here: native inside the Android app, Web Bluetooth in a
 * browser that has it, otherwise null.
 */
export function getBluetooth() {
  if (isNativeApp()) return nativeBluetooth;
  if (typeof navigator !== "undefined" && navigator.bluetooth) return navigator.bluetooth;
  return null;
}

// ─── UUIDs ─────────────────────────────────────────────────────────────

const BASE_SUFFIX = "-0000-1000-8000-00805f9b34fb";

/** Web Bluetooth accepts 0x180d, "180d" or a full UUID; the plugin wants the full, lower-case form. */
export function toFullUuid(uuid) {
  if (typeof uuid === "number") return numberToUUID(uuid);
  const s = String(uuid).toLowerCase();
  if (/^[0-9a-f]{4}$/.test(s)) return `0000${s}${BASE_SUFFIX}`;
  if (/^[0-9a-f]{8}$/.test(s)) return `${s}${BASE_SUFFIX}`;
  return s;
}

// ─── Errors ────────────────────────────────────────────────────────────

const namedError = (name, message) => {
  const err = new Error(message);
  err.name = name;
  return err;
};

const PERMISSION_HELP =
  "Khronos needs the Nearby devices permission to reach your heart-rate strap, " +
  "rower or bike. Allow it in Settings → Apps → Khronos → Permissions, then try again.";

/** The plugin's messages, reworded where a person will see them. */
function friendlyError(err) {
  const msg = String(err?.message || err || "");
  if (/cancel|no device found/i.test(msg)) {
    // Same as Chrome's picker being dismissed: the UI stays quiet.
    return namedError("NotFoundError", "No device selected.");
  }
  if (/permission/i.test(msg)) return namedError("NotAllowedError", PERMISSION_HELP);
  if (/not enabled|disabled|turned off|location services/i.test(msg)) {
    return namedError("InvalidStateError", "Bluetooth is off. Turn it on, then try again.");
  }
  return err instanceof Error ? err : new Error(msg || "Bluetooth error");
}

// ─── Plugin start-up ───────────────────────────────────────────────────

let ready = null;

/**
 * Asks for the Nearby devices permission (Android 12+) the first time
 * Bluetooth is actually needed, so nobody without gear is ever asked.
 */
function ensureReady() {
  if (!ready) {
    ready = (async () => {
      // The plugin queues every call app-wide by default, so a bike that's
      // switched off (10 s connect timeout) would hold up the strap. Each
      // device's calls are already sequential in the sessions.
      BleClient.disableQueue();
      // We never use scan results for location (manifest: neverForLocation).
      await BleClient.initialize({ androidNeverForLocation: true });
    })().catch((err) => {
      ready = null; // let the next attempt ask again
      throw friendlyError(err);
    });
  }
  return ready;
}

// ─── A tiny EventTarget ────────────────────────────────────────────────

function withEvents(target) {
  const listeners = new Map();
  target.addEventListener = (type, fn) => {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(fn);
  };
  target.removeEventListener = (type, fn) => {
    listeners.get(type)?.delete(fn);
  };
  target.dispatch = (type, event) => {
    for (const fn of [...(listeners.get(type) || [])]) {
      try {
        fn(event);
      } catch (err) {
        console.warn(`[ble:native] ${type} listener threw`, err);
      }
    }
  };
  return target;
}

// ─── Devices ───────────────────────────────────────────────────────────

// One object per device id, so listeners added to the device from
// requestDevice() and the one from getDevices() are the same listeners.
const devices = new Map();

function deviceFor(id, name) {
  let device = devices.get(id);
  if (!device) {
    device = createDevice(id, name);
    devices.set(id, device);
  } else if (name && !device.name) {
    device.name = name;
  }
  return device;
}

function createDevice(id, name) {
  const device = withEvents({ id, name: name || null });
  let services = null; // discovered on connect

  const characteristic = (serviceUuid, charUuid) => {
    const ch = withEvents({ uuid: charUuid, value: null, service: null });
    let notifying = false;
    ch.readValue = async () => {
      try {
        ch.value = await BleClient.read(id, serviceUuid, charUuid);
      } catch (err) {
        throw friendlyError(err);
      }
      return ch.value;
    };
    ch.startNotifications = async () => {
      try {
        await BleClient.startNotifications(id, serviceUuid, charUuid, (value) => {
          ch.value = value;
          ch.dispatch("characteristicvaluechanged", { target: ch });
        });
      } catch (err) {
        throw friendlyError(err);
      }
      notifying = true;
      return ch;
    };
    ch.stopNotifications = async () => {
      if (!notifying) return ch;
      notifying = false;
      await BleClient.stopNotifications(id, serviceUuid, charUuid).catch(() => {});
      return ch;
    };
    return ch;
  };

  const server = {
    device,
    get connected() {
      return services !== null;
    },
    getPrimaryService: async (uuid) => {
      const want = toFullUuid(uuid);
      const found = (services || []).find((s) => s.uuid.toLowerCase() === want);
      if (!found) throw namedError("NotFoundError", `No service ${want} on ${device.name || id}.`);
      const chars = (found.characteristics || []).map((c) => c.uuid.toLowerCase());
      return {
        uuid: want,
        device,
        isPrimary: true,
        getCharacteristic: async (charUuid) => {
          const c = toFullUuid(charUuid);
          if (!chars.includes(c)) throw namedError("NotFoundError", `No characteristic ${c}.`);
          return characteristic(want, c);
        },
        getCharacteristics: async () => chars.map((c) => characteristic(want, c)),
      };
    },
    connect: async () => {
      if (services !== null) return server; // already up, as in Chrome
      await ensureReady();
      try {
        await BleClient.connect(id, () => {
          services = null;
          device.dispatch("gattserverdisconnected", { target: device });
        });
        services = await BleClient.getServices(id);
      } catch (err) {
        services = null;
        throw friendlyError(err);
      }
      return server;
    },
    disconnect: () => {
      if (services === null) return;
      services = null;
      BleClient.disconnect(id).catch(() => {});
    },
  };

  device.gatt = server;
  return device;
}

// ─── navigator.bluetooth, natively ─────────────────────────────────────

const nativeBluetooth = {
  native: true,

  /** Shows Android's own device list, filtered like Chrome's picker. */
  async requestDevice({ filters = [], optionalServices = [] } = {}) {
    await ensureReady();
    const filter = filters[0] || {};
    let picked;
    try {
      picked = await BleClient.requestDevice({
        services: (filter.services || []).map(toFullUuid),
        name: filter.name || undefined,
        optionalServices: optionalServices.map(toFullUuid),
      });
    } catch (err) {
      throw friendlyError(err);
    }
    return deviceFor(picked.deviceId, picked.name);
  },

  /**
   * Every device this app has remembered. A native id is the device's
   * Bluetooth address, which Android can reconnect to without a fresh grant,
   * so unlike Chrome nothing here ever needs the picker again.
   */
  async getDevices() {
    const remembered = listPersistedDevices().map((p) => [p.id, p.name]);
    const hrm = readPersistedHrm();
    if (hrm?.id) remembered.push([hrm.id, hrm.name]);
    // Ask for the permission now rather than inside connect(), so a refusal
    // surfaces once per pass, not once per device.
    if (remembered.length) await ensureReady();
    return remembered.filter(([id]) => id).map(([id, name]) => deviceFor(id, name));
  },
};

/** Test hook: forget the cached device objects and start-up promise. */
export const __resetNativeBluetoothForTests = () => {
  devices.clear();
  ready = null;
};

export { nativeBluetooth };
